import { ArrowDownToLine, Eye, Pencil } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TrainersNotesStatus, UserRole, TraineeReportSortingField } from '@/apolloClient.types';
import useAppAbilities from '@/components/abilities';
import IconButton from '@/components/common/IconButton';
import type { SortOrder as TableSortOrderValue } from '@/components/common/Table/types';
import { useFullscreen } from '@/components/layouts/StackedLayout/StackedLayout';
import { useUser } from '@/contexts/UserSession';
import useCompanyRoleLabels from '@/utils/useCompanyRoleLabels';
import useDateFormats from '@/utils/useDateFormats';

export interface ListRow {
    id: string;
    userId: string;
    traineeName?: string;
    programmeName: string;
    groupName?: string;
    subGroupName?: string;
    assessmentRunName: string;
    assessmentRunId: string;
    programId?: string;
    startDate?: Date;
    endDate?: Date;
    completionDate?: Date;
    trainersNotesStatus?: TrainersNotesStatus;
    canEdit?: boolean;
}

const useColumns = (
    onView: (record: ListRow) => void,
    onDownload: (record: ListRow) => void,
    onEdit?: (record: ListRow) => void,
    sort?: { field: TraineeReportSortingField; order: any; orderValue: TableSortOrderValue | undefined }
) => {
    const { t } = useTranslation('traineeReports');
    const { formatDate } = useDateFormats();
    const abilities = useAppAbilities();
    const user = useUser(true);
    const { isMobile } = useFullscreen();
    const isTrainerPlus = user.isSuperAdmin || (user.__typename === 'TenantUser' && user.role !== UserRole.Trainee);
    const { getRoleLabel } = useCompanyRoleLabels();

    return useMemo(
        () => [
            {
                title: t('list.table.programmeName', 'Programme Name'),
                dataIndex: 'programmeName',
                key: 'programmeName',
                className: 'text-left border-b border-b-gray-300 font-semibold text-base !whitespace-normal',
                sorter: true,
                sortOrder: sort?.field === TraineeReportSortingField.Programme ? sort?.orderValue : undefined,
            },
            {
                title: t('list.table.groupName', 'Group Name'),
                dataIndex: 'groupName',
                key: 'groupName',
                className:
                    'text-left border-b border-b-gray-300 font-medium text-base text-neutral-500 !whitespace-normal',
                sorter: true,
                sortOrder: sort?.field === TraineeReportSortingField.GroupName ? sort?.orderValue : undefined,
            },
            {
                title: t('list.table.subGroupName', 'Subgroup Name'),
                dataIndex: 'subGroupName',
                key: 'subGroupName',
                className:
                    'text-left border-b border-b-gray-300 font-medium text-base text-neutral-500 !whitespace-normal',
                sorter: true,
                sortOrder: sort?.field === TraineeReportSortingField.SubGroupName ? sort?.orderValue : undefined,
            },
            {
                title: t('list.table.assessmentRun', 'Assessment Run'),
                dataIndex: 'assessmentRunName',
                key: 'assessmentRunName',
                className:
                    'text-left border-b border-b-gray-300 font-medium text-base text-neutral-500 !whitespace-normal',
                sorter: true,
                sortOrder: sort?.field === TraineeReportSortingField.AssessmentRunName ? sort?.orderValue : undefined,
            },

            {
                title: t('list.table.startDate', 'Start Date'),
                dataIndex: 'startDate',
                key: 'startDate',
                className:
                    'text-left border-b border-b-gray-300 font-medium text-base text-neutral-500 !whitespace-normal',
                render: (val?: Date) => (val ? formatDate(val) : t('emptyValue')),
                sorter: true,
                sortOrder: sort?.field === TraineeReportSortingField.StartDate ? sort?.orderValue : undefined,
            },
            {
                title: t('list.table.endDate', 'End Date'),
                dataIndex: 'endDate',
                key: 'endDate',
                className:
                    'text-left border-b border-b-gray-300 font-medium text-base text-neutral-500 !whitespace-normal',
                render: (val?: Date) => (val ? formatDate(val) : t('emptyValue')),
                sorter: true,
                sortOrder: sort?.field === TraineeReportSortingField.EndDate ? sort?.orderValue : undefined,
            },
            ...(isTrainerPlus
                ? [
                      {
                          title: t('list.table.traineeName', { role: getRoleLabel(UserRole.Trainee) }),
                          dataIndex: 'traineeName',
                          key: 'traineeName',
                          className:
                              'text-left border-b border-b-gray-300 font-medium text-base text-neutral-500 !whitespace-normal',
                          sorter: true,
                          sortOrder:
                              sort?.field === TraineeReportSortingField.TraineeName ? sort?.orderValue : undefined,
                      },
                  ]
                : []),
            {
                title: t('list.table.completionDate', 'Completion Date'),
                dataIndex: 'completionDate',
                key: 'completionDate',
                className:
                    'text-left border-b border-b-gray-300 font-medium text-base text-neutral-500 !whitespace-normal',
                render: (val?: Date) => (val ? formatDate(val) : t('emptyValue')),
                sorter: true,
                sortOrder: sort?.field === TraineeReportSortingField.CompletionDate ? sort?.orderValue : undefined,
            },
            ...(isTrainerPlus
                ? [
                      {
                          title: t('list.table.trainerNotes'),
                          dataIndex: 'trainersNotesStatus',
                          key: 'trainersNotesStatus',
                          className:
                              'text-left border-b border-b-gray-300 font-medium text-base text-neutral-500 !whitespace-normal',
                          sorter: true,
                          sortOrder:
                              sort?.field === TraineeReportSortingField.TrainersNotesStatus
                                  ? sort?.orderValue
                                  : undefined,
                          render: (_: any, record: ListRow) => {
                              switch (record.trainersNotesStatus) {
                                  case TrainersNotesStatus.None:
                                      return t('list.table.trainersNotesStatus.none');
                                  case TrainersNotesStatus.Draft:
                                      return t('list.table.trainersNotesStatus.draft');
                                  case TrainersNotesStatus.Published:
                                      return t('list.table.trainersNotesStatus.published');
                                  default:
                                      return t('list.table.trainersNotesStatus.none');
                              }
                          },
                      },
                  ]
                : []),

            {
                title: t('list.table.actions', 'Actions'),
                dataIndex: 'id',
                key: 'actions',
                className: 'text-right border-b border-b-gray-300',
                render: (_: any, record: ListRow) =>
                    abilities.canViewReports() && (
                        <div className="flex justify-end space-x-1">
                            {isTrainerPlus && (
                                <div className={`${isMobile ? 'hidden' : 'block'}`}>
                                    <IconButton
                                        onClick={() => onEdit?.(record)}
                                        title={t('actions.editBtn', 'Edit')}
                                        icon={<Pencil className="h-4 w-4 text-amber-500" strokeWidth={2} />}
                                    />
                                </div>
                            )}
                            <div className={`${isMobile ? 'block' : 'hidden'}`}>
                                <IconButton
                                    onClick={() => onDownload(record)}
                                    title={t('actions.downloadBtn')}
                                    icon={<ArrowDownToLine className="h-4 w-4 text-amber-500" strokeWidth={2} />}
                                />
                            </div>
                            <div className={`${isMobile ? 'hidden' : 'block'}`}>
                                <IconButton
                                    onClick={() => onView(record)}
                                    title={t('actions.viewBtn')}
                                    icon={<Eye className="h-4 w-4 text-blue-500" strokeWidth={2} />}
                                />
                            </div>
                        </div>
                    ),
            },
        ],
        [
            t,
            formatDate,
            abilities,
            onView,
            onDownload,
            onEdit,
            isTrainerPlus,
            sort?.field,
            sort?.orderValue,
            isMobile,
            getRoleLabel,
        ]
    );
};

export default useColumns;
